import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import { User, UserFunctions, UserService } from '@stylehaven/components';
import { SharedApplicationState } from '../../../../state/shared-state.interface';
import { LocalesService } from '../../../common/services/api/locales.service';

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
    private apiUserInfo$?: Observable<User>;
    private apiUserFunctions$?: Observable<UserFunctions>;

    constructor(
        private readonly store: Store<SharedApplicationState>,
        private readonly userService: UserService,
        private readonly translateService: SharedLanguageService,
        private readonly localesService: LocalesService
    ) {}

    public getUserInfo(): Observable<User> {
        return this.apiUserInfo$ ? this.apiUserInfo$ : this.initApiUserInfoStream();
    }

    public getUserFunctions(): Observable<UserFunctions> {
        return this.apiUserFunctions$ ? this.apiUserFunctions$ : this.initApiUserFunctionsStream();
    }

    private initApiUserInfoStream(): Observable<User> {
        const userAccessToken$: Observable<string> = this.initUserAccessTokenStream();

        this.apiUserInfo$ = userAccessToken$.pipe(
            switchMap(() => combineLatest([this.userService.initUser(), this.localesService.getLanguageLocalePairs()])),
            tap(([user, locales]) => {
                this.translateService.initSupportedLanguagesAndPairs(locales);
                this.translateService.changeLocale(user.Locale);
            }),
            map(([user, locales]) => user),
            share()
        );

        return this.apiUserInfo$;
    }

    private initApiUserFunctionsStream(): Observable<UserFunctions> {
        const userAccessToken$: Observable<string> = this.initUserAccessTokenStream();

        this.apiUserFunctions$ = userAccessToken$.pipe(
            switchMap(() => this.userService.getUserFunctions()),
            share()
        );

        return this.apiUserFunctions$;
    }

    private initUserAccessTokenStream(): Observable<string> {
        return this.store
            .select(state => state.token.userTokens.access_token)
            .pipe(
                filter(token => !!token),
                shareReplay(1)
            );
    }

    public onLogout(): void {
        this.apiUserInfo$ = undefined;
        this.apiUserFunctions$ = undefined;
    }
}
